import { useSession } from '../store/session';
import { detectLocale, useSettings, type BandPreset, type Locale } from '../store/settings';
import { en, type Dictionary, type TKey } from './en';
import { fr } from './fr';

const dictionaries: Record<Locale, Dictionary> = { en, fr };

export type Params = Record<string, string | number>;

export function translate(locale: Locale, key: TKey, params?: Params): string {
  let text: string = dictionaries[locale][key];
  if (params) {
    for (const [name, value] of Object.entries(params)) text = text.split(`{${name}}`).join(String(value));
  }
  return text;
}

export type PluralBase = 'count.cards' | 'count.boards' | 'due.late' | 'card.attempts' | 'timer.tried' | 'repeat.doneCount' | 'repeat.touchedCount';
export function translatePlural(locale: Locale, base: PluralBase, n: number): string {
  const key = `${base}.${n === 1 ? 'one' : 'other'}` as TKey;
  return translate(locale, key, { n });
}

/** The ends keep their names at any count; the middle is Neutral with three columns, else numbered. */
export function defaultColumnName(locale: Locale, index: number, count: number): string {
  if (index === 0) return translate(locale, 'column.0');
  if (index === count - 1) return translate(locale, 'column.2');
  if (count === 3) return translate(locale, 'column.1');
  return translate(locale, 'column.n', { n: index + 1 });
}

export function defaultBandLabel(locale: Locale, preset: BandPreset, index: number): string {
  return translate(locale, `band.${preset}.${index}` as TKey);
}

/** The user's own choice, else the admin's default, else the device. */
function resolveLocale(stored: Locale | null, admin: 'auto' | Locale): Locale {
  return stored ?? (admin === 'auto' ? detectLocale() : admin);
}

export function currentLocale(): Locale {
  return resolveLocale(useSettings.getState().locale, useSession.getState().defaults.locale);
}

/** The message for an API error code, falling back to a generic one. */
export function errorText(locale: Locale, code: string | undefined): string {
  const key = `auth.error.${code ?? ''}` as TKey;
  return key in dictionaries[locale] ? translate(locale, key) : translate(locale, 'auth.error.generic');
}

export interface Translator {
  locale: Locale;
  t(key: TKey, params?: Params): string;
  tn(base: PluralBase, n: number): string;
  /** The user's own name, else the admin's default for new accounts, else the localized default. */
  column(index: number): string;
  band(index: number): string;
  error(code: string | undefined): string;
}

export function useT(): Translator {
  const stored = useSettings((s) => s.locale);
  const columnLabels = useSettings((s) => s.columnLabels);
  const columnCount = useSettings((s) => s.columnCount);
  const bandLabels = useSettings((s) => s.bandLabels);
  const preset = useSettings((s) => s.bandPreset);
  const defaults = useSession((s) => s.defaults);
  const locale = resolveLocale(stored, defaults.locale);
  return {
    locale,
    t: (key, params) => translate(locale, key, params),
    tn: (base, n) => translatePlural(locale, base, n),
    column: (i) => columnLabels[i] || (columnCount === 3 ? defaults.columnLabels[i] : null) || defaultColumnName(locale, i, columnCount),
    band: (i) => bandLabels[i] || defaults.bandLabels[i] || defaultBandLabel(locale, preset, i),
    error: (code) => errorText(locale, code),
  };
}

export type { TKey };
